import {
  FEATURES,
  useGetPermissions,
  useHasPermission as hasPermission,
  FEATURE_ACTION,
} from "@ovou-inc/ovou-ui";

import { usePermissionsState } from "context";
import { useCanConfigureTemplateFeature } from "features/profile-templates/editor/useCanConfigureTemplateFeature";

export interface ITeamPermission {
  canViewCustomColor: boolean;
  canEditCustomColor: boolean;
  canViewLogo: boolean;
  canEditLogo: boolean;
  canViewCover: boolean;
  canEditCover: boolean;
  canViewTeamRequireCompanyName: boolean;
  canEditTeamRequireCompanyName: boolean;
  canViewCreditBalance: boolean;
  canViewUpgrade: boolean;
  canEditProfileTemplates: boolean;
  canViewCreateTemplate: boolean;
  canEditCreateTemplate: boolean;
  // Template-cluster feature gates (computed by useCanConfigureTemplateFeature,
  // surfaced here so all permission outcomes are inspectable in one place).
  canConfigureLeadCaptureRequireEmail: boolean;
  canConfigureLeadCaptureRequirePhone: boolean;
  canConfigureLeadCaptureQuestion: boolean;
  canConfigureLeadCaptureConsent: boolean;
  canConfigureInstantFollowupEmail: boolean;
  canConfigureCallToAction: boolean;
  canConfigureInstantLeadCapture: boolean;
}

const useTeamPermission = (): ITeamPermission => {
  const { permission } = usePermissionsState();
  const { default_permission: permissionSet } = permission || {};
  const logo = useGetPermissions(
    permissionSet || null,
    FEATURES.SETTING_BRANDING_LOGO,
  );
  const cover = useGetPermissions(
    permissionSet || null,
    FEATURES.SETTING_BRANDING_COVER,
  );

  const customColor = useGetPermissions(
    permissionSet || null,
    FEATURES.SETTING_BRANDING_CUSTOM_COLOR,
  );

  const teamRequireCompanyName = useGetPermissions(
    permissionSet || null,
    FEATURES.TEAM_REQUIRE_COMPANY_NAME,
  );

  const creditBalance = useGetPermissions(
    permissionSet || null,
    FEATURES.CREDIT_BALANCE,
  );
  const canViewUpgrade = hasPermission(
    permissionSet || null,
    FEATURES.TEAM_UPGRADE,
    FEATURE_ACTION.VIEW,
  );

  // Profile-templates page visibility (view_profile_templates) is enforced by
  // PageSetup itself, so only the edit gate is surfaced here. create_template
  // is a separate feature key carrying both view (Create button visibility)
  // and edit (creation main functionality).
  const profileTemplates = useGetPermissions(
    permissionSet || null,
    FEATURES.PROFILE_TEMPLATES,
  );
  const createTemplate = useGetPermissions(
    permissionSet || null,
    FEATURES.CREATE_TEMPLATE,
  );

  // Plan-gated template-cluster features. The computation (incl. the active
  // owner/admin tier fallback) stays in the dedicated hook; we only aggregate
  // the outcomes here so every permission decision is visible in one place.
  const canConfigureLeadCaptureRequireEmail = useCanConfigureTemplateFeature(
    FEATURES.CUSTOMIZE_LEAD_CAPTURE_FORM_REQUIRE_EMAIL,
  );
  const canConfigureLeadCaptureRequirePhone = useCanConfigureTemplateFeature(
    FEATURES.CUSTOMIZE_LEAD_CAPTURE_FORM_REQUIRE_PHONE,
  );
  const canConfigureLeadCaptureQuestion = useCanConfigureTemplateFeature(
    FEATURES.CUSTOMIZE_LEAD_CAPTURE_FORM_QUESTION,
  );
  const canConfigureLeadCaptureConsent = useCanConfigureTemplateFeature(
    FEATURES.CUSTOMIZE_LEAD_CAPTURE_FORM_CONSENT,
  );
  const canConfigureInstantFollowupEmail = useCanConfigureTemplateFeature(
    FEATURES.CUSTOMIZE_INSTANT_FOLLOWUP_EMAIL,
  );
  const canConfigureCallToAction = useCanConfigureTemplateFeature(
    FEATURES.CUSTOM_CALL_TO_ACTION,
  );
  const canConfigureInstantLeadCapture = useCanConfigureTemplateFeature(
    FEATURES.INSTANT_LEAD_CAPTURE,
  );

  return {
    canViewCustomColor: customColor?.view.enabled,
    canEditCustomColor: customColor?.edit.enabled,
    canViewLogo: logo?.view?.enabled,
    canEditLogo: logo?.edit.enabled,
    canViewCover: cover?.view?.enabled,
    canEditCover: cover?.edit?.enabled,
    canViewTeamRequireCompanyName: teamRequireCompanyName?.view?.enabled,
    canEditTeamRequireCompanyName: teamRequireCompanyName?.edit?.enabled,
    canViewCreditBalance: creditBalance?.view?.enabled,
    canViewUpgrade: canViewUpgrade,
    canEditProfileTemplates: profileTemplates?.edit?.enabled,
    canViewCreateTemplate: createTemplate?.view?.enabled,
    canEditCreateTemplate: createTemplate?.edit?.enabled,
    canConfigureLeadCaptureRequireEmail,
    canConfigureLeadCaptureRequirePhone,
    canConfigureLeadCaptureQuestion,
    canConfigureLeadCaptureConsent,
    canConfigureInstantFollowupEmail,
    canConfigureCallToAction,
    canConfigureInstantLeadCapture,
  };
};
export default useTeamPermission;
